/**
 * Служебный экран фазы 0: проверка платформы.
 *
 * Показывает состояние зависимостей и позволяет прогнать тестовую джобу
 * по пути API → Redis → воркер. Удаляется, когда фаза 0 закрыта.
 */
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { PageHeader } from '../components/ui/PageHeader';
import { StatusDot } from '../components/ui/StatusDot';
import { systemApi } from '../api/system';
import { jobsApi } from '../api/jobs';
import { useJobStatus, jobStatusLabel } from '../features/jobs/useJobStatus';
import { useTranslation } from 'react-i18next';
import { errorText } from '../i18n/errors';

/** Имя проверки приходит с сервера: держим ключ, переводим при отрисовке. */
const checkKey = (name: string): string => `system.check.${name}`;

export function SystemPage() {
  const { t } = useTranslation();
  const [jobId, setJobId] = useState<string | null>(null);
  const [generationId, setGenerationId] = useState<string | null>(null);

  const health = useQuery({
    queryKey: ['health'],
    queryFn: systemApi.health,
    refetchInterval: 10_000,
    retry: false,
  });

  const jobState = useQuery({
    queryKey: ['ping-job', jobId],
    queryFn: () => systemApi.pingState(jobId as string),
    enabled: jobId !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'completed' || status === 'failed' ? false : 700;
    },
  });

  const enqueue = useMutation({
    mutationFn: () => systemApi.enqueuePing('phase-0 check'),
    onSuccess: (job) => setJobId(job.jobId),
  });

  const generation = useJobStatus(generationId);

  const startGeneration = useMutation({
    mutationFn: () => jobsApi.startTestImage(t('system.testPrompt')),
    onSuccess: (job) => setGenerationId(job.id),
  });

  const cancelGeneration = useMutation({
    mutationFn: () => jobsApi.cancel(generationId as string),
  });

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title={t('system.title')}
        description={t('system.subtitle')}
      />

      <Card>
        <CardBody className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h2 className="text-card-title">{t('system.dependencies')}</h2>
            <span className="text-meta text-content-muted">
              {health.data ? t('system.version', { value: health.data.version }) : ''}
            </span>
          </div>

          {health.isError ? (
            <p className="text-body text-error">
              {errorText(health.error)}
            </p>
          ) : null}

          <div className="flex flex-col gap-2">
            {Object.entries(health.data?.checks ?? {}).map(([key, value]) => (
              <div key={key} className="flex items-center gap-2 text-body">
                <StatusDot tone={value === 'ok' ? 'ok' : 'fail'} />
                <span className="text-content">{t(checkKey(key), { defaultValue: key })}</span>
                <span className="text-content-muted">{value === 'ok' ? t('system.up') : t('system.down')}</span>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="flex flex-col gap-4">
          <h2 className="text-card-title">{t('system.testJob')}</h2>
          <p className="text-body text-content-secondary">{t('system.testJobNote')}</p>

          <div className="flex items-center gap-3">
            <Button
              variant="primary"
              onClick={() => enqueue.mutate()}
              disabled={enqueue.isPending}
            >
              {enqueue.isPending ? t('system.enqueuing') : t('system.run')}
            </Button>
            {jobId ? <span className="text-meta text-content-muted">job {jobId}</span> : null}
          </div>

          {enqueue.isError ? (
            <p className="text-body text-error">{errorText(enqueue.error)}</p>
          ) : null}

          {jobState.data ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 text-body">
                <StatusDot
                  tone={
                    jobState.data.status === 'completed'
                      ? 'ok'
                      : jobState.data.status === 'failed'
                        ? 'fail'
                        : 'warn'
                  }
                />
                <span className="text-content">{jobState.data.status}</span>
              </div>
              {jobState.data.result ? (
                <pre className="overflow-x-auto rounded-control border border-line bg-surface-secondary p-4 text-meta text-content-secondary">
                  {JSON.stringify(jobState.data.result, null, 2)}
                </pre>
              ) : null}
            </div>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardBody className="flex flex-col gap-4">
          <h2 className="text-card-title">{t('system.generationPath')}</h2>
          <p className="text-body text-content-secondary">
            {t('system.pathNote')}
          </p>

          <div className="flex items-center gap-3">
            <Button
              variant="primary"
              onClick={() => startGeneration.mutate()}
              disabled={startGeneration.isPending}
            >
              {startGeneration.isPending ? t('system.starting') : t('system.generateImage')}
            </Button>
            {generation.data && generation.data.stage ? (
              <Button
                variant="tertiary"
                onClick={() => cancelGeneration.mutate()}
                disabled={cancelGeneration.isPending}
              >{t('system.cancel')}</Button>
            ) : null}
          </div>

          {startGeneration.isError ? (
            <Alert tone="error">{errorText(startGeneration.error)}</Alert>
          ) : null}

          {generation.data ? (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2 text-body">
                <StatusDot
                  tone={
                    generation.data.status === 'completed'
                      ? 'ok'
                      : generation.data.status === 'failed'
                        ? 'fail'
                        : 'warn'
                  }
                />
                <span className="text-content">{jobStatusLabel(generation.data)}</span>
                <span className="ml-auto text-meta text-content-muted">
                  {t('system.reserveSpent', {
                    reserved: generation.data.unitsReserved,
                    spent: generation.data.unitsSpent,
                  })}
                </span>
              </div>

              {/* §69: тонкая полоса, без декоративных эффектов */}
              <div className="progress-track">
                <div className="progress-bar" style={{ width: `${generation.data.progress}%` }} />
              </div>

              {generation.data.result ? (
                <pre className="overflow-x-auto rounded-control border border-line bg-surface-secondary p-4 text-meta text-content-secondary">
                  {JSON.stringify(generation.data.result, null, 2)}
                </pre>
              ) : null}
            </div>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
